// @ts-strict-ignore
import {nanoid} from 'nanoid';
import isNil from 'lodash/isNil';
import pick from 'lodash/pick';

import type {CustomEventDefinition} from 'common/report/util/saved-entities/types';
import type {DataGroup, SelectWarning} from 'common/widgets/data-group-selector/types';
import {insertAtIndex, removeByIndex, replaceByIndex} from 'common/query-builder/cohorts-query-builder/util/index';
import {
  isCohortLiteral,
  isEventLiteral,
  isPeopleLiteral,
  isSerializedCohortLiteral,
  isSerializedEventLiteral,
} from 'common/query-builder/cohorts-query-builder/util/staged-cohort';

import {LITERAL_NAMES} from '../constants';
import {FrequencyOperator} from '../enums/builder-enums';
import {BehaviorCountType, LogicalOperator, PredefinedCohort, WindowUnit} from '../enums/expression-enums';
import {OperatorNode} from '../expression/operator-node';
import type {Behavior} from '../behaviors/behavior';
import type {
  ClauseOperators,
  FilterLabelPart,
  FrequencyClauseBuilderState,
  StagedBehaviorDateRange,
  StagedPropertyFilterClause,
} from '../types/builder-state-types';
import type {CohortId, EventFrequency, ExpressionTree} from '../types/expression-types';
import type {Serializable, SerializedCohort, SerializedInlineCohort, User} from '../types/serialized-cohort-types';
import type {SerializedStagedCohort} from '../types/serialized-staged-cohort-types';
import type {Trackable} from '../types/tracking';
import {Clause} from './clause';
import {CohortLiteral} from './cohort-literal';
import {EventLiteral} from './event-literal';
import {PeopleLiteral} from './people-literal';
import type {CohortClause} from './cohort-clause';
import type {EventFrequencyClause} from './event-frequency-clause';
import type {Literal} from './literal';
import type {PropertyFilter} from './property-filter';

export interface ClausePosition {
  literalIdx: number;
  clauseIdx: number;
}

interface StagedCohortOptions {
  id?: number;
  unsavedId?: string;
  name?: string;
  description?: string;
  literals?: Literal[];
  operators?: LogicalOperator[];
  dependentCohortIds?: number[];
  dataGroupId?: string;
  createdBy?: User;

  // permissions
  can_view?: boolean;
  can_update_basic?: boolean;
  can_share?: boolean;
  is_superadmin?: boolean;
  allow_staff_override?: boolean;
}

export interface ValidationArgs {
  dataGroupId: string;
  customEvents?: CustomEventDefinition[];
  cohorts?: Array<SerializedCohort | SerializedInlineCohort>;
}

type DataGroupSelectWarningArgs = {dataGroups: DataGroup[]; stagedCohort: StagedCohort} & Omit<
  Required<ValidationArgs>,
  `dataGroupId`
>;

export class StagedCohort implements Serializable, ExpressionTree, Trackable {
  public static deserialize(
    options: SerializedStagedCohort,
    {dependentCohortIds = []}: {dependentCohortIds?: number[]} = {},
  ): StagedCohort {
    if (isNil(options)) {
      return new StagedCohort({dependentCohortIds});
    }

    const literals = options.literals.map((literal) => {
      if (isSerializedEventLiteral(literal)) {
        return EventLiteral.deserialize(literal);
      } else if (isSerializedCohortLiteral(literal)) {
        return CohortLiteral.deserialize(literal);
      }
      return PeopleLiteral.deserialize(literal);
    });

    return new StagedCohort({
      dependentCohortIds,
      description: options.description,
      id: options.id,
      unsavedId: options.unsavedId,
      literals,
      name: options.name,
      operators: options.operators,
      ...(options.data_group_id ? {dataGroupId: options.data_group_id} : {}),
    });
  }

  public static generateDataGroupSelectWarnings({
    stagedCohort,
    dataGroups,
    customEvents,
    cohorts,
  }: DataGroupSelectWarningArgs): SelectWarning[] {
    return dataGroups.map((dataGroup) => {
      const dataGroupId = dataGroup.groupID;
      return {
        dataGroupId,
        showWarning: !stagedCohort.isValidForDataGroup({dataGroupId, customEvents, cohorts}),
      };
    });
  }

  public readonly id: number = null;
  public readonly unsavedId: string = ``;
  public readonly name = ``;
  public readonly description = ``;
  public readonly literals: Literal[] = [];
  public readonly operators: LogicalOperator[] = [];
  public readonly dependentCohortIds: number[] = [];
  public declare readonly expressionLabel: string;
  public declare readonly dataGroupId?: string;
  public readonly createdBy: User = null;

  constructor(options: StagedCohortOptions = {}) {
    Object.assign(this, options);
    if (this.numLiterals === 0) {
      this.literals = [this.getNewLiteral()];
    }

    this.expressionLabel = this.getExpressionLabel();
  }

  get eventLiterals(): EventLiteral[] {
    return this.literals.filter((l) => isEventLiteral(l)) as EventLiteral[];
  }

  get cohortLiterals(): CohortLiteral[] {
    return this.literals.filter((l) => isCohortLiteral(l)) as CohortLiteral[];
  }

  get peopleLiterals(): PeopleLiteral[] {
    return this.literals.filter((l) => isPeopleLiteral(l)) as PeopleLiteral[];
  }

  // region Cohort operations

  public includesReferenceToCohortId(id: number): boolean {
    return this.cohortLiterals.some((cohortLiteral) =>
      cohortLiteral.clauses.map((clause) => clause.cohortId).includes(id),
    );
  }

  public includesSelfReference(): boolean {
    return this.includesReferenceToCohortId(this.id);
  }

  public getExpressionTree(): OperatorNode {
    const buildTreeFromComponents = (components: ExpressionTree[], operator: LogicalOperator): OperatorNode => {
      if (!components.length) {
        return null;
      }

      return components
        .slice(1)
        .reduce(
          (root, component) => new OperatorNode(operator, root, component.getExpressionTree()),
          components[0].getExpressionTree(),
        );
    };

    let literalIdx = 0;
    const andOperatorSequences = this.operators.join(``).split(String(LogicalOperator.Or));
    return andOperatorSequences.reduce((root: OperatorNode, andOperators) => {
      const andOperatorSeqLength = andOperators.split(String(LogicalOperator.And)).length - 1;
      const literalArray = this.literals.slice(literalIdx, literalIdx + andOperatorSeqLength + 1);
      const tree = buildTreeFromComponents(literalArray, LogicalOperator.And);
      literalIdx = literalIdx + andOperatorSeqLength + 1;
      return !root ? tree : new OperatorNode(LogicalOperator.Or, root, tree);
    }, null);
  }

  public serialize(): SerializedStagedCohort {
    return {
      description: this.description,
      id: this.id,
      literals: this.literals.map((l) => l.serialize()),
      unsavedId: this.unsavedId,
      name: this.name,
      operators: this.operators,
      ...(this.dataGroupId ? {data_group_id: this.dataGroupId} : {}),
    };
  }

  public updateDetails({
    name = this.name,
    description = this.description,
    unsavedId = this.unsavedId,
  }: {
    name?: string;
    description?: string;
    unsavedId?: string;
  }): StagedCohort {
    return this.newStagedCohort({description, name, unsavedId});
  }

  public setUnsavedIdIfNotSet(unsavedId = nanoid()): StagedCohort {
    if (this.unsavedId) {
      return this;
    }

    return this.updateDetails({unsavedId});
  }

  public isValid(): boolean {
    return this.literals.every((l) => l.isValid());
  }

  public toTrackingData(): Obj {
    return {
      'all users literals': this.numAllUsersLiterals,
      [`and's`]: this.operators.filter((operator) => operator === LogicalOperator.And).length,
      clauses: this.numClauses,
      'cohort id': this.id,
      'cohort literals': this.numCohortLiterals,
      'event literals': this.eventLiterals.length,
      'expression label': this.expressionLabel,
      funnels: this.eventLiterals.filter((literal) => literal.isFunnel).length,
      literals: this.numLiterals,
      [`or's`]: this.operators.filter((operator) => operator === LogicalOperator.Or).length,
      'people literals': this.peopleLiterals.length,
      'property filters': this.eventLiterals.reduce((count, literal) => {
        return literal.clauses.reduce((subcount, clause) => subcount + clause.getPropertyFilters().length, count);
      }, 0),
      b2b: !!this.dataGroupId,
    };
  }

  public toggleOperator(operatorIdx: number) {
    const operator = this.operators[operatorIdx];
    const newOperator = operator === LogicalOperator.And ? LogicalOperator.Or : LogicalOperator.And;
    return this.newStagedCohort({
      operators: replaceByIndex(this.operators, operatorIdx, newOperator),
    });
  }

  public get numClauses(): number {
    return this.literals.reduce((count, literal) => count + literal.clauses.length, 0);
  }

  public get numLiterals(): number {
    return this.literals.length;
  }

  public get numAllUsersLiterals(): number {
    return this.cohortLiterals.reduce((count, literal) => {
      return literal.clauses.reduce((subcount, clause) => {
        return subcount + (clause.cohortId === PredefinedCohort.AllUsers ? 1 : 0);
      }, count);
    }, 0);
  }

  public get numCohortLiterals(): number {
    return this.cohortLiterals.length - this.numAllUsersLiterals;
  }

  // endregion

  // region Literal operations

  public addLiteral(literal = this.getNewLiteral()): StagedCohort {
    return this.newStagedCohort({
      literals: this.literals.concat([literal]),
      operators: this.operators.concat([LogicalOperator.And]),
    });
  }

  public moveLiteral(fromLiteralIdx: number, toLiteralIdx: number): StagedCohort {
    const numOperators = this.operators.length;
    const fromOperatorIdx = fromLiteralIdx === numOperators ? numOperators - 1 : fromLiteralIdx;
    const toOperatorIdx = toLiteralIdx === numOperators ? numOperators - 1 : toLiteralIdx;
    const movingLiteral = this.literals[fromLiteralIdx];
    const movingOperator = this.operators[fromOperatorIdx];
    return this.newStagedCohort({
      literals: insertAtIndex(removeByIndex(this.literals, fromLiteralIdx), toLiteralIdx, movingLiteral),
      operators: insertAtIndex(removeByIndex(this.operators, fromOperatorIdx), toOperatorIdx, movingOperator),
    });
  }

  public removeLiteral(literalIdx: number): StagedCohort {
    const leftOperatorIdx = Math.max(0, literalIdx - 1);
    const rightOperatorIdx = literalIdx;
    const rightOperator = this.operators[rightOperatorIdx];
    const operatorIdx = rightOperator === LogicalOperator.And ? rightOperatorIdx : leftOperatorIdx;
    return this.newStagedCohort({
      literals: removeByIndex(this.literals, literalIdx),
      operators: this.operators.length > 0 ? removeByIndex(this.operators, operatorIdx) : [],
    }).updateLabelPrefixes();
  }

  public removeLiterals(literalIndexes: number[]): StagedCohort {
    literalIndexes = [...literalIndexes].sort((a, b) => b - a);
    return literalIndexes.reduce((stagedCohort, i) => stagedCohort.removeLiteral(i), this);
  }

  public restoreLiteral(eventLiteral: EventLiteral): StagedCohort {
    return this.newStagedCohort({
      literals: this.literals.concat([eventLiteral]),
      operators: this.operators.concat([LogicalOperator.And]),
    }).updateLabelPrefixes();
  }

  public addClauseToLiteralAtIndex(literalIdx: number): StagedCohort {
    const newLiteral = this.literals[literalIdx].addDefaultClause();
    return this.replaceLiteral(newLiteral, literalIdx);
  }

  // endregion

  public removeClause({literalIdx, clauseIdx}: ClausePosition): StagedCohort {
    const newLiteral = this.literals[literalIdx].removeClauseByIndex(clauseIdx);
    return newLiteral.clauses.length ? this.replaceLiteral(newLiteral, literalIdx) : this.removeLiteral(literalIdx);
  }

  public toggleClauseNegated(clausePosition: ClausePosition): StagedCohort {
    const {literalIdx, clauseIdx} = clausePosition;
    const negated = !this.getClause(clausePosition).isNegated();
    const newLiteral = this.getLiteral(clausePosition).setClauseNegated(negated, clauseIdx);
    return this.replaceLiteral(newLiteral, literalIdx);
  }

  public getClauseOperators(literalIdx: number): ClauseOperators {
    return this.literals[literalIdx].getClauseOperators();
  }

  public togglePropertyFilterOperator({literalIdx, clauseIdx}: ClausePosition): StagedCohort {
    const newLiteral = this.literals[literalIdx].togglePropertyFilterOperator(clauseIdx);
    return this.replaceLiteral(newLiteral, literalIdx);
  }

  public updateClauseOperators(clauseOperators: ClauseOperators, literalIdx: number): StagedCohort {
    const newLiteral = this.literals[literalIdx].setClauseOperators(clauseOperators);
    return this.replaceLiteral(newLiteral, literalIdx);
  }

  public updateLabelPrefixes(): StagedCohort {
    const literals = this.literals.map((literal, idx) => literal.setLabelPrefix(LITERAL_NAMES[idx]));
    return this.newStagedCohort({literals});
  }

  public getTrackingDataForLiteral(literalIdx: number) {
    return {
      ...{'literal index': literalIdx},
      ...this.literals[literalIdx].toTrackingData(),
    };
  }

  // region Data Group helpers

  public isValidForDataGroup({dataGroupId, customEvents, cohorts}: Required<ValidationArgs>): boolean {
    return this.literals.every((l) => !l.isInvalidForDataGroup({dataGroupId, customEvents, cohorts}));
  }

  public makeValidForDataGroup({dataGroupId, customEvents, cohorts}: Required<ValidationArgs>): StagedCohort {
    const updatedLiterals = this.literals.map((l) => l.sanitizeForDataGroup({dataGroupId, customEvents, cohorts}));

    const literalIndexesToRemove = updatedLiterals.reduce((accum, literal, i) => {
      return literal.clauses.length === 0 ? accum.concat(i) : accum;
    }, []);
    const finalLiterals = updatedLiterals.filter((_l, i) => !literalIndexesToRemove.includes(i));

    // We must call #removeLiterals() (which also updates operators) before #newStagedCohort(), otherwise there is an
    // error in the StagedCohort constructor when it calls #getExpressionLabel().
    const intermediateStagedCohort = this.removeLiterals(literalIndexesToRemove);
    return intermediateStagedCohort.newStagedCohort({literals: finalLiterals, dataGroupId}).updateLabelPrefixes();
  }

  // endregion

  // region Cohort literal helpers

  public getCohortId(clausePosition: ClausePosition): CohortId {
    return this.getClause<CohortClause>(clausePosition).cohortId;
  }

  // endregion

  // region Event literal helpers

  public getBehavior(clausePosition: ClausePosition): Behavior {
    return this.getClause<EventFrequencyClause>(clausePosition).behavior;
  }

  public getFrequency(clausePosition: ClausePosition): EventFrequency {
    return this.getClause<EventFrequencyClause>(clausePosition).frequency;
  }

  public getFrequencyOperator(clausePosition: ClausePosition): FrequencyOperator {
    return this.getClause<EventFrequencyClause>(clausePosition).frequencyOperator;
  }

  public getFrequencyClauseBuilderState(clausePosition: ClausePosition): FrequencyClauseBuilderState {
    return this.getClause<EventFrequencyClause>(clausePosition).builderState;
  }

  public getFrequencyClauseEventValue(clausePosition: ClausePosition): string {
    return this.getClause<EventFrequencyClause>(clausePosition).behavior?.getEvent();
  }

  public getStagedBehaviorDateRange(clausePosition: ClausePosition): StagedBehaviorDateRange {
    return this.getClause<EventFrequencyClause>(clausePosition).stagedBehaviorDateRange;
  }

  public hasValidStagedDateRange(clausePosition: ClausePosition): boolean {
    return this.getClause<EventFrequencyClause>(clausePosition).hasValidStagedDateRange();
  }

  public updateCohort(cohortId: CohortId, clausePosition: ClausePosition): StagedCohort {
    const {literalIdx, clauseIdx} = clausePosition;
    if (clauseIdx !== 0) {
      throw new Error(`Cohort must be at clauseIdx 0`);
    }

    const literal = this.literals[literalIdx];
    let newLiteral;
    if (isCohortLiteral(literal)) {
      newLiteral = literal;
    } else {
      newLiteral = CohortLiteral.createDefaultWithPrefix(literal.labelPrefix);
    }

    let isDependentCohort = false;
    if (this.isUserDefinedCohortId(cohortId)) {
      isDependentCohort = this.dependentCohortIds.includes(cohortId) || cohortId === this.id;
    }

    newLiteral = newLiteral.setCohortId({cohortId, clauseIdx, isDependentCohort});

    return this.replaceLiteral(newLiteral, literalIdx);
  }

  public updateEvent(event: string, clausePosition: ClausePosition): StagedCohort {
    const {literalIdx, clauseIdx} = clausePosition;
    const literal = this.literals[literalIdx];
    let newLiteral;
    if (isEventLiteral(this.literals[literalIdx])) {
      newLiteral = this.getLiteral<EventLiteral>(clausePosition).setBehaviorCountEvent(event, clauseIdx);
    } else {
      newLiteral = EventLiteral.createDefaultWithPrefix(literal.labelPrefix).setBehaviorCountEvent(event, clauseIdx);
    }

    return this.replaceLiteral(newLiteral, literalIdx);
  }

  public updateBehaviorCountType(behaviorCountType: BehaviorCountType, clausePosition: ClausePosition): StagedCohort {
    const {literalIdx, clauseIdx} = clausePosition;
    const newLiteral = this.getLiteral<EventLiteral>(clausePosition).setBehaviorCountType(behaviorCountType, clauseIdx);
    return this.replaceLiteral(newLiteral, literalIdx);
  }

  public updateBehaviorCustomRange(unit: WindowUnit, value: number, clausePosition: ClausePosition): StagedCohort {
    const {literalIdx, clauseIdx} = clausePosition;
    const newLiteral = this.getLiteral<EventLiteral>(clausePosition).setBehaviorCustomRange(unit, value, clauseIdx);
    return this.replaceLiteral(newLiteral, literalIdx);
  }

  /**
   * Updates the behavior date range from a given date to another date
   * @param fromDate string date in the format YYYY-MM-DD (e.g., 2017-07-01)
   * @param toDate string date in the format YYYY-MM-DD (e.g., 2017-07-01)
   * @param clausePosition
   */
  public updateBehaviorDateRange(fromDate: string, toDate: string, clausePosition: ClausePosition): StagedCohort {
    const {literalIdx, clauseIdx} = clausePosition;
    const newLiteral = this.getLiteral<EventLiteral>(clausePosition).setBehaviorDateRange(fromDate, toDate, clauseIdx);
    return this.replaceLiteral(newLiteral, literalIdx);
  }

  public updateStagedBehaviorDateRange(options: StagedBehaviorDateRange, clausePosition: ClausePosition): StagedCohort {
    const {literalIdx, clauseIdx} = clausePosition;
    const newLiteral = this.getLiteral<EventLiteral>(clausePosition).setStagedBehaviorDateRange(options, clauseIdx);
    return this.replaceLiteral(newLiteral, literalIdx);
  }

  public updateFrequencyOperator(frequencyOperator: FrequencyOperator, clausePosition: ClausePosition): StagedCohort {
    const {literalIdx, clauseIdx} = clausePosition;
    const newLiteral = this.getLiteral<EventLiteral>(clausePosition).setFrequencyOperator(frequencyOperator, clauseIdx);
    return this.replaceLiteral(newLiteral, literalIdx);
  }

  public updateFrequencyValue(frequency: EventFrequency, clausePosition: ClausePosition): StagedCohort {
    const {literalIdx, clauseIdx} = clausePosition;
    const newLiteral = this.getLiteral<EventLiteral>(clausePosition).setFrequencyValue(frequency, clauseIdx);
    return this.replaceLiteral(newLiteral, literalIdx);
  }

  public updateFrequencyState(builderState: FrequencyClauseBuilderState, clausePosition: ClausePosition): StagedCohort {
    const {literalIdx, clauseIdx} = clausePosition;
    const newLiteral = this.getLiteral<EventLiteral>(clausePosition).setFrequencyState(builderState, clauseIdx);
    return this.replaceLiteral(newLiteral, literalIdx);
  }

  public addPropertyFilterToClause<T extends CohortLiteral | EventLiteral>(
    clausePosition: ClausePosition,
  ): StagedCohort {
    const {literalIdx, clauseIdx} = clausePosition;
    const newLiteral = this.getLiteral<T>(clausePosition).addPropertyFilter(clauseIdx);
    return this.replaceLiteral(newLiteral, literalIdx);
  }

  public getTrackingDataForClause(clausePosition: ClausePosition) {
    const {literalIdx, clauseIdx} = clausePosition;
    return {
      ...this.getTrackingDataForLiteral(literalIdx),
      ...{'clause index': clauseIdx},
      ...this.getClause(clausePosition).toTrackingData(),
    };
  }

  // endregion

  // region Property filter helpers

  public getPropertyFilterLabelParts(clausePosition: ClausePosition, propertyFilterIdx = 0): FilterLabelPart[] {
    return this.getClause(clausePosition).getFilterLabelParts(propertyFilterIdx);
  }

  public getNumPropertyFilters(clausePosition: ClausePosition): number {
    return this.getClause(clausePosition).getPropertyFilters().length;
  }

  public getPropertyFilterOperator(clausePosition: ClausePosition): LogicalOperator {
    return this.getClause(clausePosition).propertyFilterOperator;
  }

  public getPropertyName(clausePosition: ClausePosition, propertyFilterIdx = 0): string {
    return this.getClause(clausePosition).getPropertyFilterProperty(propertyFilterIdx);
  }

  public getProperty(clausePosition: ClausePosition, propertyFilterIdx = 0): PropertyFilter {
    return this.getClause(clausePosition).getPropertyFilters()[propertyFilterIdx];
  }

  public getStagedFilterClause(
    clausePosition: ClausePosition,
    propertyFilterIdx: number | null = 0,
  ): StagedPropertyFilterClause {
    return this.getClause(clausePosition).getStagedFilterClause(propertyFilterIdx);
  }

  getPropertyFilter(clausePosition: ClausePosition, propertyFilterIdx = 0) {
    return this.getClause(clausePosition).getPropertyFilters()[propertyFilterIdx];
  }

  public getTrackingDataForPropertyFilter(clausePosition: ClausePosition, propertyFilterIdx = 0) {
    return {
      ...this.getTrackingDataForClause(clausePosition),
      ...{'property filter index': propertyFilterIdx},
      ...this.getPropertyFilter(clausePosition, propertyFilterIdx).toTrackingData(),
    };
  }

  public removePropertyFilter(clausePosition: ClausePosition, propertyFilterIdx = 0): StagedCohort {
    const {literalIdx, clauseIdx} = clausePosition;
    const newLiteral = this.getLiteral(clausePosition).removePropertyFilter(clauseIdx, propertyFilterIdx);
    return this.replaceLiteral(newLiteral, literalIdx);
  }

  // endregion

  public commitStagedFilterClause(clausePosition: ClausePosition, propertyFilterIdx: number | null = 0): StagedCohort {
    const {literalIdx, clauseIdx} = clausePosition;
    const literal = this.literals[literalIdx];
    let newLiteral;
    if (propertyFilterIdx !== null) {
      newLiteral = this.getLiteral(clausePosition).commitStagedFilterClause(clauseIdx, propertyFilterIdx);
    } else {
      // adding a people property to an event literal
      const {stagedPeoplePropertyFilter} = this.getClause<CohortClause | EventFrequencyClause>(clausePosition);
      if (!stagedPeoplePropertyFilter.hasInvalidStagedFilterClause()) {
        newLiteral = PeopleLiteral.createDefaultWithPrefix(literal.labelPrefix)
          .setStagedFilterClause(stagedPeoplePropertyFilter.getStagedFilterClause(), 0, 0)
          .commitStagedFilterClause(0, 0);
      } else {
        newLiteral = literal;
      }
    }

    return this.replaceLiteral(newLiteral, literalIdx);
  }

  public updateStagedFilterClause(
    stagedClauseOptions: Partial<StagedPropertyFilterClause>,
    clausePosition: ClausePosition,
    propertyFilterIdx: number | null = 0,
  ): StagedCohort {
    const {literalIdx, clauseIdx} = clausePosition;
    const newLiteral = this.getLiteral(clausePosition).setStagedFilterClause(
      stagedClauseOptions,
      clauseIdx,
      propertyFilterIdx,
    );
    return this.replaceLiteral(newLiteral, literalIdx);
  }

  public replaceLiteral(newLiteral: Literal, idx: number): StagedCohort {
    const literals = replaceByIndex(this.literals, idx, newLiteral);
    return this.newStagedCohort({literals});
  }

  public getNextLabelPrefix(
    index = Math.max(...this.literals.map((l) => LITERAL_NAMES.indexOf(l.labelPrefix)), -1) + 1,
  ): string {
    return LITERAL_NAMES[index];
  }

  // region Helpers

  private isUserDefinedCohortId(cohortId: CohortId): cohortId is number {
    return typeof cohortId === `number`;
  }

  private getExpressionLabel(): string {
    const getLiteralLabel = (literal: Literal) => literal.labelPrefix;
    const getOperatorClauseLabel = (literals: Literal[], operator: LogicalOperator) =>
      literals.map(getLiteralLabel).join(` ${String(operator).toLowerCase()} `);

    const andOperatorParts = this.operators.join(``).split(String(LogicalOperator.Or));
    if (andOperatorParts.length === 1) {
      // no operators, or only "and" operators
      return getOperatorClauseLabel(this.literals, LogicalOperator.And);
    }

    let literalIdx = 0;
    const numAndOperatorParts = andOperatorParts.length;
    return andOperatorParts.reduce((label, andOperators, idx) => {
      const andOperatorSeqLength = andOperators.split(String(LogicalOperator.And)).length - 1;
      const literals = this.literals.slice(literalIdx, literalIdx + andOperatorSeqLength + 1);
      const addTrailingOrOperator = idx !== numAndOperatorParts - 1;
      let clauseLabel = ``;
      if (andOperatorSeqLength > 0) {
        clauseLabel = `(${getOperatorClauseLabel(literals, LogicalOperator.And)})`;
      } else {
        clauseLabel = getLiteralLabel(literals[0]);
      }
      literalIdx = literalIdx + andOperatorSeqLength + 1;
      return `${label}${clauseLabel}${addTrailingOrOperator ? ` or ` : ``}`;
    }, ``);
  }

  private getLiteral<T extends Literal>(clausePosition: ClausePosition): T {
    return this.literals[clausePosition.literalIdx] as T;
  }

  private getClause<U extends Clause>(clausePosition: ClausePosition): U {
    return this.getLiteral(clausePosition).clauses[clausePosition.clauseIdx] as U;
  }

  private getNewLiteral(): Literal {
    return CohortLiteral.createDefaultWithPrefix(this.getNextLabelPrefix());
  }

  private newStagedCohort(options: Partial<StagedCohortOptions>): StagedCohort {
    const oldOptions = pick(this, [`name`, `unsavedId`, `description`, `literals`, `operators`, `id`, `dataGroupId`]);
    return new StagedCohort({...oldOptions, ...options});
  }

  // endregion
}
